import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  pulseOffset: number;
}

const PARTICLE_COUNT = 28;
const MAX_DIST = 100;
const TARGET_FPS = 30;
const FRAME_INTERVAL = 1000 / TARGET_FPS;

export default function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animId: number;
    let particles: Particle[] = [];
    let W = 0;
    let H = 0;
    let tick = 0;
    let lastTime = 0;
    let isVisible = true;

    const resize = () => {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };

    const init = () => {
      resize();
      particles = Array.from({ length: PARTICLE_COUNT }, () => ({
        x: Math.random() * W,
        y: Math.random() * H,
        vx: (Math.random() - 0.5) * 0.38,
        vy: (Math.random() - 0.5) * 0.38,
        size: Math.random() * 1.6 + 0.3,
        opacity: Math.random() * 0.40 + 0.07,
        pulseOffset: Math.random() * Math.PI * 2,
      }));
    };

    const draw = (timestamp: number) => {
      animId = requestAnimationFrame(draw);

      // Skip frame if tab is hidden or hero not visible
      if (!isVisible || document.hidden) return;

      // Throttle to TARGET_FPS
      if (timestamp - lastTime < FRAME_INTERVAL) return;
      lastTime = timestamp;

      tick++;
      ctx.clearRect(0, 0, W, H);

      // Connection lines — skip on every other tick to halve CPU cost
      if (tick % 2 === 0) {
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            // Use squared distance to avoid sqrt when possible
            const distSq = dx * dx + dy * dy;
            if (distSq < MAX_DIST * MAX_DIST) {
              const dist = Math.sqrt(distSq);
              const alpha = (1 - dist / MAX_DIST) * 0.18;
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.strokeStyle = `rgba(0,207,255,${alpha})`;
              ctx.lineWidth = 0.6;
              ctx.stroke();
            }
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        const pulse = Math.sin(tick * 0.025 + p.pulseOffset) * 0.12;
        const currentOpacity = Math.max(0.04, p.opacity + pulse);

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,207,255,${currentOpacity})`;
        ctx.fill();
      }

      // Move particles
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > W) p.vx *= -1;
        if (p.y < 0 || p.y > H) p.vy *= -1;
      }
    };

    // Pause when hero scrolls out of view
    const visibilityObserver = new IntersectionObserver(
      ([entry]) => { isVisible = entry.isIntersecting; },
      { threshold: 0 }
    );
    visibilityObserver.observe(canvas);

    init();
    animId = requestAnimationFrame(draw);

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
      visibilityObserver.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.65 }}
    />
  );
}
